import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { adminDestForPath } from '../features/admin-hub/adminDests';
import { useAuth, type AuthUser } from '../features/auth/AuthContext';
import { canAccessAttachments, canAccessPageByPerm } from '../lib/permissions';
import { usePermissions } from '../features/auth/usePermissions';
import { ForcePasswordChange } from '../features/auth/ForcePasswordChange';
import { CommandPalette, useCommandPaletteHotkey } from './CommandPalette';
import { OnboardingBanner } from '../features/onboarding/OnboardingBanner';
import { TenantOnboardingBanner } from '../features/onboarding/TenantOnboardingBanner';
import { TrialBanner } from '../features/subscription/TrialBanner';
import { PaywallOverlay } from '../features/subscription/PaywallOverlay';
import { UpgradeModal } from '../features/features/UpgradeModal';
import { EarlyAccessBanner } from '../features/early-access/EarlyAccessBanner';
import { FoundingBadge } from '../features/subscription/FoundingBadge';
import { OnboardingWizard } from '../features/onboarding/OnboardingWizard';
import { WelcomeTour } from '../features/onboarding/WelcomeTour';
import { InstallPrompt } from './InstallPrompt';
import { Logo } from './Logo';
import { NotificationBell } from '../features/notifications/NotificationBell';
import { HelpButton } from '../features/help/HelpButton';
import clsx from 'clsx';

/** Seções do menu lateral. 23 itens numa lista plana viravam uma parede;
 * o agrupamento é só de LEITURA (o gate real continua por permissão/visible).
 * 'rodape' é fixado embaixo (Suporte/Ajuda). Sem label = sem cabeçalho. */
type NavGroup =
  | 'inicio'
  | 'atendimento'
  | 'comercial'
  | 'financeiro'
  | 'admin'
  | 'rodape';

const NAV_GROUPS: { key: NavGroup; label?: string }[] = [
  { key: 'inicio' },
  { key: 'atendimento', label: 'Atendimento' },
  { key: 'comercial', label: 'Comercial e IA' },
  { key: 'financeiro', label: 'Financeiro' },
  { key: 'admin' },
];

interface NavItem {
  to: string;
  label: string;
  group: NavGroup;
  visible?: (user: AuthUser | null) => boolean;
}

interface NavItemWithIcon extends NavItem {
  icon: React.ReactNode;
}

const I = {
  dashboard: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
  box: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  ),
  chat: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12a9 9 0 01-13.5 7.8L3 21l1.2-4.5A9 9 0 1121 12z" />
    </svg>
  ),
  calendar: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  users: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 3a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  briefcase: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.93 23.93 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  clipboard: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  invoice: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21l-7-3-7 3V5a2 2 0 012-2h10a2 2 0 012 2v16z" />
    </svg>
  ),
  team: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  tooth: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3c-2.5 0-4 1.2-4 3 0 1 .5 2 .5 4 0 3-1 6-1 8 0 2 .8 3 1.8 3s1.5-2 1.7-4c.2-1.6.5-2 1-2s.8.4 1 2c.2 2 .7 4 1.7 4s1.8-1 1.8-3c0-2-1-5-1-8 0-2 .5-3 .5-4 0-1.8-1.5-3-4-3z" />
    </svg>
  ),
  folder: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  ),
  shield: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  chart: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  megaphone: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
    </svg>
  ),
  doc: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  cog: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  plug: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  lifeBuoy: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636A9 9 0 105.636 18.364 9 9 0 0018.364 5.636zM15 12a3 3 0 11-6 0 3 3 0 016 0zM4.93 4.93l3.54 3.54m6.36 6.36l3.54 3.54M4.93 19.07l3.54-3.54m6.36-6.36l3.54-3.54" />
    </svg>
  ),
  storefront: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7l1.5-3h15L21 7M3 7v12a1 1 0 001 1h16a1 1 0 001-1V7M3 7h18M9 21V12h6v9" />
    </svg>
  ),
  funnel: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18l-7 8v6l-4 2v-8L3 4z" />
    </svg>
  ),
  help: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  scale: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v18m-7-4h14M5 7l-2 6a3 3 0 006 0L7 7m10 0l-2 6a3 3 0 006 0l-2-6M12 7l5-2m-5 2L7 5" />
    </svg>
  ),
};

const nav: NavItemWithIcon[] = [
  { to: '/painel', label: 'Painel', icon: I.dashboard, group: 'inicio' },

  { to: '/agenda', label: 'Agenda', icon: I.calendar, group: 'atendimento' },
  { to: '/pacientes', label: 'Pacientes', icon: I.users, group: 'atendimento' },
  { to: '/prontuario', label: 'Prontuário', icon: I.tooth, group: 'atendimento' },
  { to: '/planos', label: 'Planos', icon: I.clipboard, group: 'atendimento' },
  { to: '/arquivos', label: 'Arquivos', icon: I.folder, group: 'atendimento', visible: (u) => canAccessAttachments(u) },
  { to: '/estoque', label: 'Estoque', icon: I.box, group: 'atendimento' },

  { to: '/crm', label: 'Funil de vendas', icon: I.funnel, group: 'comercial' },
  { to: '/atendimentos-ia', label: 'Atendimentos IA', icon: I.chat, group: 'comercial' },
  { to: '/campanhas', label: 'Campanhas', icon: I.megaphone, group: 'comercial' },
  // Aposentados (v1.7.1): /templates era órfão (não entregava à Meta) e
  // /marketplace é prematuro (efeito-de-rede sem base). Escondidos, não
  // removidos — flip `visible` p/ religar. Rotas também saíram do App.tsx.
  { to: '/templates', label: 'Templates', icon: I.doc, group: 'comercial', visible: () => false },
  { to: '/marketplace', label: 'Marketplace', icon: I.storefront, group: 'comercial', visible: () => false },

  { to: '/faturas', label: 'Faturas', icon: I.invoice, group: 'financeiro' },
  { to: '/contas-a-pagar', label: 'Contas a pagar', icon: I.invoice, group: 'financeiro' },
  { to: '/outras-receitas', label: 'Outras receitas', icon: I.invoice, group: 'financeiro' },
  { to: '/conciliacao', label: 'Conciliação', icon: I.scale, group: 'financeiro' },
  { to: '/relatorios', label: 'Relatórios', icon: I.chart, group: 'financeiro' },

  // Catálogo, equipe e configurações viram UMA entrada → página /administracao
  // (hub em cards). Enxuga o menu; os destinos continuam gateados por permissão
  // dentro do hub. Aberto a todos (Serviços é acessível a qualquer papel).
  { to: '/administracao', label: 'Administração', icon: I.cog, group: 'admin' },

  { to: '/suporte', label: 'Suporte', icon: I.lifeBuoy, group: 'rodape' },
  { to: '/ajuda', label: 'Ajuda', icon: I.help, group: 'rodape' },
];

const ROLE_META: Record<AuthUser['role'], { label: string; cls: string }> = {
  owner: { label: 'Dono', cls: 'bg-accent-100 text-accent-800 border-accent-200' },
  manager: { label: 'Gestor', cls: 'bg-violet-100 text-violet-800 border-violet-200' },
  dentist: { label: 'Dentista', cls: 'bg-brand-100 text-brand-800 border-brand-200' },
  assistant: { label: 'Assistente', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  receptionist: { label: 'Recepção', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
};

function initialsOf(email?: string): string {
  if (!email) return '-';
  const base = email.split('@')[0];
  const parts = base.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

/**
 * Badge do item "Atendimentos IA": nº de conversas vivas (agente + aguardando)
 * e um ponto pulsando quando há alguém esperando a equipe. Só é montado quando
 * o item está visível (usuário com agent.read), então não gera 403 pra quem
 * não deveria ver a inbox. Poll leve de 20s, como o sino.
 */
function AgentInboxBadge() {
  const { data } = useQuery<{ open: number; waiting: number; active: number }>({
    queryKey: ['agent', 'summary'],
    queryFn: async () => (await api.get('/agent/conversations/summary')).data,
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
  const active = data?.active ?? 0;
  const waiting = data?.waiting ?? 0;

  // Badge no ícone do app instalado (PWA) = nº aguardando a equipe. É o sinal
  // que a recepção vê sem abrir o sistema, atendendo do celular. Feature-detect
  // + try/catch: só existe em Chrome/Edge e no PWA instalado (iOS 16.4+), e
  // lança em alguns contextos. Limpa quando zera. Roda antes do early-return
  // pra também limpar quando não há conversa ativa.
  useEffect(() => {
    const nav = navigator as Navigator & {
      setAppBadge?: (n?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (!nav.setAppBadge) return;
    try {
      if (waiting > 0) void nav.setAppBadge(waiting)?.catch(() => undefined);
      else void nav.clearAppBadge?.()?.catch(() => undefined);
    } catch {
      /* contexto sem suporte — ignora */
    }
  }, [waiting]);

  if (active <= 0) return null;
  return (
    <span
      className={clsx(
        'ml-auto inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold',
        waiting > 0 ? 'bg-amber-500 text-white' : 'bg-ink-200 text-ink-700',
      )}
      title={
        waiting > 0
          ? `${waiting} aguardando a equipe · ${active} ativas`
          : `${active} conversas ativas`
      }
    >
      {waiting > 0 && (
        <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
        </span>
      )}
      {active > 99 ? '99+' : active}
    </span>
  );
}

/** Item de menu (link). Extraído pra ser reutilizado nas seções agrupadas. */
function NavItemLink({
  item,
  onClick,
}: {
  item: NavItemWithIcon;
  onClick: () => void;
}) {
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      className={({ isActive }) =>
        clsx(
          'group relative flex items-center gap-3 px-3 py-2.5 sm:py-2 rounded-lg text-sm transition-all',
          isActive
            ? 'bg-gradient-to-r from-brand-50 to-accent-50/50 text-brand-800 font-medium shadow-sm'
            : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-gradient-to-b from-brand-500 to-accent-500" aria-hidden="true" />
          )}
          <span
            className={clsx(
              'flex-shrink-0 transition-colors',
              isActive ? 'text-brand-600' : 'text-ink-400 group-hover:text-ink-600',
            )}
          >
            {item.icon}
          </span>
          <span className="truncate">{item.label}</span>
          {item.to === '/atendimentos-ia' && <AgentInboxBadge />}
        </>
      )}
    </NavLink>
  );
}

/** Trilha "Administração / {destino}" nas páginas abertas pelo hub de cards.
 * Sem ela, quem entra por /administracao → Serviços fica sem volta (o item
 * saiu do menu lateral): o breadcrumb devolve o atalho pro hub. */
function AdminBreadcrumb() {
  const { pathname } = useLocation();
  const dest = adminDestForPath(pathname);
  if (!dest) return null;
  return (
    <nav
      className="mb-4 flex items-center gap-1.5 text-sm"
      aria-label="Trilha de navegação"
    >
      <Link
        to="/administracao"
        className="inline-flex items-center gap-1 text-ink-500 hover:text-brand-600 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Administração
      </Link>
      <span className="text-ink-300" aria-hidden="true">/</span>
      <span className="font-medium text-ink-700" aria-current="page">
        {dest.label}
      </span>
    </nav>
  );
}

export function AppShell() {
  const { user, logout } = useAuth();
  const { can, permissions, isLoading: permsLoading } = usePermissions();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { open: paletteOpen, setOpen: setPaletteOpen } = useCommandPaletteHotkey();

  const closeSidebar = () => setSidebarOpen(false);
  // Sidebar por papel (2026-06-12): além do `visible` específico do
  // item, TODO item passa pelo PAGE_ACCESS (espelho dos @Roles do
  // backend) — assistente/recepção não veem Configurações, Integrações,
  // Equipe, Relatórios etc. que só renderiam 403.
  const visibleNav = useMemo(
    () =>
      nav.filter(
        (item) =>
          // Enquanto carrega as permissões, mostra tudo (evita flicker);
          // resolvido, filtra pelas permissões efetivas (papel custom).
          (permsLoading || canAccessPageByPerm(can, item.to)) &&
          (item.visible ? item.visible(user) : true),
      ),
    // `permissions` (ref estável do react-query) no dep evita recomputo à toa.
    [user, permissions, permsLoading],
  );

  // Gate de troca de senha obrigatória (1º acesso com senha temporária).
  if (user?.mustChangePassword) {
    return <ForcePasswordChange />;
  }

  return (
    <div className="min-h-screen flex bg-ink-50">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink-900/50 backdrop-blur-sm md:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-ink-200 flex flex-col',
          'transform transition-transform duration-200 ease-in-out',
          'md:relative md:translate-x-0 md:w-60',
          sidebarOpen ? 'translate-x-0 shadow-pop' : '-translate-x-full md:translate-x-0',
        )}
        aria-label="Navegação principal"
      >
        <div className="relative px-4 py-3.5 border-b border-ink-200 flex items-center justify-between bg-gradient-to-br from-white to-brand-50/30">
          <span className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-brand-500 via-accent-500 to-brand-600" aria-hidden="true" />
          <Link
            to="/painel"
            className="inline-flex items-center gap-2"
            aria-label="Kevins Clínicas"
            onClick={closeSidebar}
          >
            <Logo variant="lockup" size={32} />
            <FoundingBadge />
          </Link>
          <button
            type="button"
            onClick={closeSidebar}
            className="md:hidden -mr-2 p-2.5 sm:p-2 text-ink-500 hover:text-ink-900 rounded-md hover:bg-ink-100"
            aria-label="Fechar menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <nav className="flex-1 p-2 overflow-y-auto">
          {NAV_GROUPS.map((grp) => {
            const items = visibleNav.filter((i) => i.group === grp.key);
            if (items.length === 0) return null;
            return (
              <div key={grp.key} className="mb-1">
                {grp.label && (
                  <p className="px-3 pt-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-ink-400 select-none">
                    {grp.label}
                  </p>
                )}
                <div className="space-y-0.5">
                  {items.map((item) => (
                    <NavItemLink key={item.to} item={item} onClick={closeSidebar} />
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
        {visibleNav.some((i) => i.group === 'rodape') && (
          <div className="p-2 border-t border-ink-200 flex gap-1">
            {visibleNav
              .filter((i) => i.group === 'rodape')
              .map((item) => (
                <div key={item.to} className="flex-1">
                  <NavItemLink item={item} onClick={closeSidebar} />
                </div>
              ))}
          </div>
        )}
        <div className="border-t border-ink-200 bg-gradient-to-br from-white to-ink-50/50">
          <div className="px-3 py-3 flex items-center gap-3">
            <Link
              to="/perfil"
              onClick={closeSidebar}
              className="group flex items-center gap-3 flex-1 min-w-0 rounded-lg p-1 -m-1 hover:bg-ink-100/60 transition-colors"
              aria-label="Meu perfil"
              title="Meu perfil"
            >
              <div
                className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-brand-500 to-accent-600 text-white text-sm font-semibold flex items-center justify-center shadow-sm ring-2 ring-white group-hover:ring-brand-200 transition-shadow"
                aria-hidden="true"
              >
                {initialsOf(user?.email)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-ink-800 truncate group-hover:text-brand-700 transition-colors" title={user?.email}>
                  {user?.email}
                </div>
                {user?.role && (
                  <span
                    className={clsx(
                      'mt-0.5 inline-block px-1.5 py-0.5 rounded-md text-[10px] font-semibold border',
                      ROLE_META[user.role]?.cls,
                    )}
                  >
                    {ROLE_META[user.role]?.label ?? user.role}
                  </span>
                )}
              </div>
            </Link>
            <button
              type="button"
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="flex-shrink-0 p-2.5 sm:p-2 rounded-md text-ink-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              aria-label="Sair"
              title="Sair"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 w-full min-w-0 flex flex-col">
        <header className="h-14 border-b border-ink-200 bg-white sticky top-0 z-20 flex items-center px-3 md:px-6 gap-2">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 -ml-2 rounded-md text-ink-600 hover:text-ink-900 hover:bg-ink-100"
            aria-label="Abrir menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <Link
            to="/painel"
            className="md:hidden inline-flex items-center"
            aria-label="Kevins Clínicas"
          >
            <Logo variant="mark" size={28} />
          </Link>

          <h2 className="hidden md:block text-sm font-medium text-ink-700">
            Clínica
          </h2>

          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            data-testid="open-command-palette"
            className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-md border border-ink-200 bg-white text-xs text-ink-500 hover:bg-ink-50 hover:text-ink-700 hover:border-brand-300 hover:shadow-sm transition-all"
            title="Busca rápida (Ctrl+K)"
            aria-label="Busca rápida"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="hidden sm:inline">Buscar…</span>
            <span className="hidden md:inline text-[10px] font-mono text-ink-400 border border-ink-200 rounded px-1.5 py-0.5">
              Ctrl+K
            </span>
          </button>

          <HelpButton />
          <NotificationBell />
        </header>

        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        <PaywallOverlay />
        <UpgradeModal />
        <TrialBanner />
        <EarlyAccessBanner />
        <TenantOnboardingBanner />
        <OnboardingBanner />

        <div className="flex-1 min-w-0 overflow-x-clip p-3 sm:p-4 md:p-6 lg:p-8">
          <AdminBreadcrumb />
          <Outlet />
        </div>

        <footer className="border-t border-ink-200 px-4 py-3 text-center text-[11px] text-ink-400">
          © {new Date().getFullYear()} Kevins Clínicas · Desenvolvido por{' '}
          <a
            href="https://i9atech.com"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-ink-500 hover:text-brand-600"
          >
            i9aTech
          </a>
        </footer>
      </main>

      <InstallPrompt />
      {/* Wizard fullscreen na primeira visita do owner. Renderiza-se */}
      {/* sozinho condicionalmente; pode ser pulado sem bloquear nav. */}
      <OnboardingWizard />
      {/* Tour passo a passo dos módulos no 1º acesso, filtrado por permissão. */}
      {/* Pro dono fica o wizard acima; demais papéis veem o tour. */}
      <WelcomeTour />
    </div>
  );
}
